import {
  Server,
  Clock,
  MapPin,
  Settings
} from 'lucide-react';

const StationCard = ({ station, onSelect }) => {

  // Fonction pour calculer le temps écoulé depuis la dernière vue
  const getTimeAgo = (lastSeen) => {
    if (!lastSeen) return 'Jamais';
    
    const now = new Date();
    const lastSeenDate = new Date(lastSeen);
    const diffMs = now - lastSeenDate;
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return 'À l\'instant';
    if (diffMinutes < 60) return `${diffMinutes}m`;
    if (diffHours < 24) return `${diffHours}h`;
    return `${diffDays}j`;
  };

  const isOffline = station.status === 'offline';
  const isOnline = station.status === 'online' || station.status === 'healthy';

  return (
    <div 
      className={`card cursor-pointer relative overflow-hidden group
        ${isOffline ? 'bg-primary-50 border-primary-300 opacity-75' : 'bg-fonteyne-white'}
        ${station.status === 'critical' ? 'border-warning-400 bg-warning-50 shadow-warning-200/50' : ''}
        ${isOnline ? 'border-success-300 shadow-success-100/30' : ''}
      `}
      onClick={() => onSelect && onSelect(station)}
    >
      {/* Header Premium avec nom et statut */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <div className={`flex-shrink-0 p-3 rounded-xl ${
            isOffline ? 'bg-primary-100' : 
            isOnline ? 'bg-success-100' : 
            'bg-warning-100'
          }`}>
            <Server className={`h-6 w-6 ${
              isOffline ? 'text-primary-500' : 
              isOnline ? 'text-success-600' : 
              'text-warning-600'
            }`} />
          </div>
          <div>
            <h3 className={`text-xl font-display font-bold ${
              isOffline ? 'text-fonteyne-medium-gray' : 'text-fonteyne-black'
            }`}>
              {station.name}
            </h3>
            <p className="text-sm font-medium text-fonteyne-medium-gray tracking-wide uppercase">
              {station.station_id}
            </p>
          </div>
        </div>
        
        <div className="flex flex-col items-end space-y-2">
          {/* Badge de statut élégant */}
          <span className={`status-${station.status} font-semibold`}>
            {station.status}
          </span>
          
          {/* Indicateur d'actions avec style premium */}
          <div className="flex items-center space-x-1 text-xs text-fonteyne-medium-gray bg-primary-50 px-2 py-1 rounded-full">
            <Settings className="h-3 w-3" />
            <span className="font-medium">Gérer</span>
          </div>
        </div>
      </div>

      {/* Informations élégantes de la station */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        <div className="flex items-center space-x-3 bg-primary-25 p-3 rounded-lg">
          <div className="p-2 bg-primary-100 rounded-full">
            <MapPin className="h-4 w-4 text-primary-600" />
          </div>
          <div>
            <div className="text-xs font-medium text-fonteyne-medium-gray uppercase tracking-wide">Localisation</div>
            <div className="text-sm font-semibold text-fonteyne-charcoal">
              {station.location || 'Non configurée'}
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-3 bg-primary-25 p-3 rounded-lg">
          <div className="p-2 bg-primary-100 rounded-full">
            <Clock className="h-4 w-4 text-primary-600" />
          </div>
          <div>
            <div className="text-xs font-medium text-fonteyne-medium-gray uppercase tracking-wide">Dernière activité</div>
            <div className="text-sm font-semibold text-fonteyne-charcoal">
              {getTimeAgo(station.last_seen)}
            </div>
          </div>
        </div>
      </div>

      {/* Métriques de performance sophistiquées */}
      {station.metrics && (
        <div className="border-t-2 border-primary-100 pt-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="text-xs font-semibold text-fonteyne-medium-gray uppercase tracking-wider mb-1">CPU</div>
              <div className={`text-lg font-bold ${
                station.metrics.cpu_load_1min > 0.8 ? 'text-warning-600' : 'text-success-600'
              }`}>
                {station.metrics.cpu_load_1min ? `${(station.metrics.cpu_load_1min * 100).toFixed(0)}%` : '—'}
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-xs font-semibold text-fonteyne-medium-gray uppercase tracking-wider mb-1">Mémoire</div>
              <div className={`text-lg font-bold ${
                station.metrics.memory_percent > 80 ? 'text-warning-600' : 'text-success-600'
              }`}>
                {station.metrics.memory_percent ? `${station.metrics.memory_percent.toFixed(0)}%` : '—'}
              </div>
            </div>
            
            <div className="text-center">
              <div className="text-xs font-semibold text-fonteyne-medium-gray uppercase tracking-wider mb-1">Stockage</div>
              <div className={`text-lg font-bold ${
                station.metrics.disk_percent > 90 ? 'text-warning-600' : 'text-success-600'
              }`}>
                {station.metrics.disk_percent ? `${station.metrics.disk_percent.toFixed(0)}%` : '—'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Indicateur de commandes en attente premium */}
      {station.pending_commands > 0 && (
        <div className="absolute top-4 right-4">
          <div className="bg-accent-400 text-fonteyne-charcoal text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-lg border-2 border-fonteyne-white">
            {station.pending_commands}
          </div>
        </div>
      )}

      {/* Indicateur de connexion sophistiqué */}
      <div className="absolute bottom-4 right-4">
        <div className={`h-3 w-3 rounded-full border-2 border-fonteyne-white shadow-md ${
          isOnline ? 'bg-success-400 animate-pulse-slow' : 'bg-primary-300'
        }`}></div>
      </div>

      {/* Effet de survol premium */}
      <div className="absolute inset-0 bg-gradient-to-r from-accent-100/0 to-accent-100/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg"></div>
    </div>
  );
};

export default StationCard;

